import BaseLayout from '@/layout/BaseLayout';
import type { UserDto } from '@shelflix/trpc/routes/user/list/useCase';
import { Badge } from '@shelflix/ui-web/components/ui/badge';

import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { trpc } from '@/trpcClient';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import type { ListUserFilterTypeWithValues } from '@shelflix/schemas/user/list';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@shelflix/ui-web/components/ui/tabs';
import { SwipeableTabs } from '@/components/SwipeableTabs';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RoleList } from './RoleList';
import { UserFilter } from './UserFilter';
import { UserHierarchy } from '../UserHierarchy';

export function UserList() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [activeTab, setActiveTab] = useState('users');

  const {
    data: roles = [],
    isLoading: isLoadingRoles,
    isError: _isErrorRoles,
    isFetched: isRolesFetched,
    refetch: updateRoles,
  } = useQuery(trpc.role.list.queryOptions({ sort: { field: 'hierarchy', direction: 'asc' } }));

  const [filters, setFilters] = useState<ListUserFilterTypeWithValues>({
    sort: { field: 'lastLoginAt', direction: 'desc' },
  });

  const { data, isLoading, isError } = useQuery(
    trpc.user.list.queryOptions(
      {
        page,
        recordsPerPage: pageSize,
        ...(filters.search && { search: { ...filters.search } }),
        ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
        sort: { field: 'lastLoginAt', direction: filters.sort?.direction ?? 'desc' },
      },
      { enabled: isRolesFetched }
    )
  );

  const users = data?.users ?? [];
  const totalItems = data?.total ?? 0;

  // Columns for user table - tenant-isolated, no cross-tenant data
  const columns: Array<{
    header: string;
    key: keyof UserDto;
    render?: (value: UserDto[keyof UserDto], row: UserDto) => React.ReactNode;
  }> = [
    {
      header: 'Nome',
      key: 'name',
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]">
            {row.name}
          </span>
          <span className="[font-family:'Figtree',Helvetica] text-xs font-normal text-[var(--gray-primary)]">
            {row.email}
          </span>
          {row.registration && (
            <span className="[font-family:'Figtree',Helvetica] text-xs font-normal text-[var(--gray-primary)]">
              Matrícula: {row.registration}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      key: 'active',
      render: (active) => (
        <Badge
          className={`h-6 w-[95px] rounded-sm px-0 py-[5px] text-xs font-medium ${
            active === true
              ? 'bg-[var(--active)] text-[var(--active-dark)]'
              : 'bg-[var(--inactive)] text-[var(--inactive-dark)]'
          }`}
        >
          {active === true ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      header: 'Cargo',
      key: 'role',
      render: (_, row) => (
        <div className="[font-family:'Figtree'] text-base text-[var(--gray-primary)]">{row.role.name}</div>
      ),
    },
    {
      header: 'Último Acesso',
      key: 'lastLoginAt',
      render: (_, row) => (
        <div className="[font-family:'Figtree',Helvetica] text-base text-[var(--gray-primary)]">
          {row.lastLoginAt && dayjs(row.lastLoginAt.toString()).format('DD/MM/YYYY - HH:mm')}
        </div>
      ),
    },
    {
      header: 'Ações',
      key: 'uuid',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="h-8 w-8 rounded-md p-0 hover:bg-[var(--secondary)]"
            onClick={() => handleEdit(row.uuid)}
          >
            <Icon name="edit" className="text-primary" />
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (uuid: string) => {
    navigate(`${uuid}`);
  };

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success('Alterações salvas com sucesso');
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const { isMobile } = useDeviceDetect();

  return (
    <BaseLayout>
      <section className="flex flex-col gap-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="w-full overflow-hidden">
            <TabsList className="ml-4 md:ml-0">
              <TabsTrigger value="users">Usuários</TabsTrigger>
              <TabsTrigger value="roles">Cargos</TabsTrigger>
              <TabsTrigger value="hierarchy">Hierarquia</TabsTrigger>
            </TabsList>
          </div>

          <SwipeableTabs currentTab={activeTab} tabs={['users', 'roles', 'hierarchy']} onTabChange={setActiveTab}>
            {/* Tab Users */}
            <div className="mt-6 space-y-6 px-4 md:px-0">
              {isRolesFetched && (
                <UserFilter
                  roles={roles}
                  onChange={(filters) => {
                    setFilters(filters);
                    setPage(1);
                  }}
                />
              )}
              <section className="w-full">
                {isLoading && <p className="p-4">Carregando usuários...</p>}
                {isError && <p className="p-4 text-red-500">Erro ao carregar usuários.</p>}
                {!isLoading && !isError && (
                  <GenericTable
                    columns={columns}
                    data={users}
                    onPageChange={setPage}
                    itemsPerPages={pageSize}
                    totalItems={totalItems}
                    page={page}
                  />
                )}
              </section>
            </div>

            {/* Tab Roles */}
            <div className="mt-6 space-y-6 px-4 md:px-0">
              <section className="w-full">
                <Card className="rounded-[10px] border-none shadow-none">
                  <CardHeader className="pb-4">
                    <div className="space-between flex items-center">
                      <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                        Hierarquia de cargos
                      </CardTitle>
                      {!isMobile && (
                        <RestrictedComponent requiredScopes={['admin:roles:create']}>
                          <div className="ml-auto">
                            <Button
                              className="bg-primary hover:bg-primary flex cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white"
                              onClick={() => navigate('/role/create')}
                            >
                              <Icon name="admin_panel_settings" fill={1} className="text-white" size={20} />
                              <span className="text-white">Cadastrar novo cargo</span>
                            </Button>
                          </div>
                        </RestrictedComponent>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingRoles && <p className="p-4">Carregando cargos...</p>}
                    {isError && <p className="p-4 text-red-500">Erro ao carregar cargos.</p>}
                    {!isLoadingRoles && !isError && <RoleList roles={roles} onRoleChanged={() => updateRoles()} />}
                    {isMobile && (
                      <RestrictedComponent requiredScopes={['admin:roles:create']}>
                        <div className="mt-6 flex justify-center">
                          <Button
                            className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:grow-0"
                            onClick={() => navigate('/role/create')}
                          >
                            <Icon name="admin_panel_settings" fill={1} className="text-white" size={20} />
                            <span className="text-white">Cadastrar novo cargo</span>
                          </Button>
                        </div>
                      </RestrictedComponent>
                    )}
                  </CardContent>
                </Card>
              </section>
            </div>

            <div className="mt-6 space-y-6 px-4 md:px-0">
              <section className="w-full">
                <UserHierarchy />
              </section>
            </div>
          </SwipeableTabs>
        </Tabs>
      </section>
    </BaseLayout>
  );
}
